import { ReactElement, useEffect, useState, useRef, useContext } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import Dropzone from "react-dropzone";
import SinglePagePDFPreview from "./SinglePagePdfPreview";
import Toast from "shared/helpers/Toast";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { LoanService } from "services/LoanService";
import { DefaultPagination } from "services";
import Utils from "shared/helpers/Utils";
import { DocumentStatusCode } from "shared/helpers/Constants";
import DocumentPreview from "./DocumentPreview";
import EmptyPlaceholder from "shared/components/EmptyPlaceholder";
import { useProgress } from "context/ProgressProvider";
import { UploadDocumentContext } from "./UploadDocuments";
import {
  AppPrimaryButton,
  BlackTooltip,
} from "shared/components/AppComponents";
import ThankYouDialog from "./ThankYouDialog";

export interface DocumentsProps {
  _id: string;
  documentName: string;
  documentDescription: string;
  instructions: any;
  docType: number;
  isDefault: boolean;
  isAppliedPerProperty: boolean;
  isPayout: boolean;
  isRefinance: boolean;
  isInterestOnly: boolean;
  isAdminInitiated: boolean;
  isAdminUploadForSign: boolean;
  instructionsForDownload: string[];
}

export interface UploadedDocumentsProps {
  loanDocumentId: string;
  applicantName: string;
  applicantEmail: string;
  requestedBy: string;
  requestedTime: string;
  loanId: string;
  applicationId: number;
  submittedBy: string;
  documents: DocumentsProps;
  documentId: string;
  createdAt: string;
  blobFilePath: string;
  blobFilePathToDownload: string;
  uploadedTime: string;
  uploadedBy: string;
  documentStatus: number;
  uploadedByName: string;
  adminApproval: {
    actionedBy: string;
    reason: string;
    status: number;
  };
  isDownloaded: false;
  propertyIndex: number;
  propertyAddress: string;
  securityPropertyId: string;
  isPhysicalCopyMailed: boolean;
}

export const getDocumentStatusColorCode = (status: number): ReactElement => (
  <Box className="status-chip-container">
    {status === DocumentStatusCode.REQUESTED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Requested</Typography>
          </>
        }
        className="pending"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.NEEDCLARIFICATION && (
      <Chip
        label={
          <>
            <Typography variant="body1">Need clarification</Typography>
          </>
        }
        className="rejected"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.UPLOADED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Uploaded</Typography>
          </>
        }
        className="started"
        variant="filled"
        size="small"
      />
    )}
    {status === DocumentStatusCode.ACCEPTED && (
      <Chip
        label={
          <>
            <Typography variant="body1">Approved</Typography>
          </>
        }
        className="verified"
        variant="filled"
        size="small"
      />
    )}
  </Box>
);

const DocumentUpload = (): ReactElement => {
  const { setActiveStep }: any = useContext<any>(UploadDocumentContext);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { handleCompleteAndNext, currentProfile } = useProgress();

  const allowedFileType = "application/pdf";
  const maxPDFFileSize = 10; // MB

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(
    {} as UploadedDocumentsProps
  );
  const [statusDocuments, setStatusDocuments] = useState("pending");
  const [statusPhysicalDocuments, setStatusPhysicalDocuments] = useState(false);
  const hiddenFileInput: any = useRef(null);
  const [pagination, setPagination] = useState({
    ...DefaultPagination(20),
  });
  const [loanDocumentsList, setLoanDocumentsList] = useState<
    UploadedDocumentsProps[]
  >([]);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showThankyouDialog, setShowThankyouDialog] = useState("");

  // Instance method
  const getDocumentStatus = (documents: UploadedDocumentsProps[]) => {
    const relevantDocs = documents.filter(
      (doc) =>
        doc.documents.isAdminUploadForSign ||
        doc.documents.isAdminInitiated ||
        doc.documents.isDefault
    );

    if (relevantDocs.length === 0) {
      return "progress";
    }

    const containsPending = relevantDocs.some(
      (doc) => doc.documentStatus === 0 || doc.documentStatus === 2
    );
    const allProgress = relevantDocs.every((doc) => doc.documentStatus === 1);
    const allCompleted = relevantDocs.every((doc) => doc.documentStatus === 3);

    let statusDocument = "";

    if (containsPending) {
      statusDocument = "pending";
    } else if (allProgress) {
      statusDocument = "progress";
    } else if (allCompleted) {
      statusDocument = "completed";
    } else {
      statusDocument = "progress";
    }

    return statusDocument;
  };

  // API Calls
  const loadDocumentsList = (): void => {
    setLoadingHistory(false);
    LoanService.getAllMappedDocuments(
      pagination.offset,
      pagination.limit,
      currentProfile.loadId
    )
      .then((response: any) => {
        const { documents, pagination } = response.data.data;
        const updatedDocumentStatus = getDocumentStatus(documents);
        console.log("updatedDocumentStatus");
        setStatusDocuments(updatedDocumentStatus);
        setLoanDocumentsList(documents);
        setPagination(pagination);

        //Getting physical update
        const filteredDocuments = documents.filter(
          (loan) => loan?.documents?.isAdminUploadForSign
        );
        const physicalMailed = filteredDocuments.every(
          (loan) => loan?.isPhysicalCopyMailed === true
        );
        setStatusPhysicalDocuments(physicalMailed);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  };

  // UI Actions
  const uploadDocument = (data: any, callback: any): void => {
    LoanService.uploadLoanDocument(data)
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  const downloadSignedDocuments = (data, callback) => {
    LoanService.downloadSignedDocuments(data)
      .then((response: any) => {
        callback(true);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        callback(true);
      })
      .finally(() => {});
  };

  // UI Actions
  const actionOnUpload = (event: any): void => {
    if (event.target.files) {
      const file = event.target.files[0];
      setIsUploading(true);

      const formData = new FormData();

      formData.append("loanId", currentProfile.loadId);
      formData.append("file", file);
      formData.append("loanDocumentId", selectedDocument.loanDocumentId);

      uploadDocument(formData, (status: any) => {
        if (status) {
        }
        setSelectedFile("");
        setSelectedDocument({} as UploadedDocumentsProps);
        setIsUploading(false);
        loadDocumentsList();
      });
    }
  };

  const actionRemoveFile = (event: any): void => {
    event.stopPropagation();
    setSelectedFiles([]);
  };

  const actionOnView = (document: UploadedDocumentsProps): void => {
    const downloadURL =
      document.documentStatus === DocumentStatusCode.REQUESTED
        ? document.blobFilePathToDownload
        : document.blobFilePath;

    setSelectedDocument({
      ...document,
      blobFilePathToDownload: downloadURL,
    });
    setShowDocumentPreview(true);
  };

  const onCloseView = (): void => {
    setShowDocumentPreview(false);
    loadDocumentsList();
  };

  const actionOnDowload = async (
    document: UploadedDocumentsProps
  ): Promise<void> => {
    setIsDownloading(true);
    setSelectedDocument(document);
    const requestPayload = {
      loanId: document.loanId,
      loanDocumentId: document.loanDocumentId,
    };
    downloadSignedDocuments(requestPayload, async () => {
      const downloadURL =
        document.documentStatus === DocumentStatusCode.REQUESTED
          ? document.blobFilePathToDownload
          : document.blobFilePath;
      if (downloadURL) {
        await saveAs(downloadURL, `${document?.documents?.documentName}.pdf`);
        setTimeout(() => {
          setSelectedDocument({} as UploadedDocumentsProps);
          setIsDownloading(false);
        }, 1000);
        loadDocumentsList();
      }
    });
  };

  useEffect(() => {
    loadDocumentsList();
  }, []);

  useEffect(() => {
    //
  }, [selectedDocument]);

  // UI Elements
  const getSignedDocumentList = (loanDocument: UploadedDocumentsProps): any => (
    <Box className="document-upload-wrapper">
      <Box className="document-upload-section py-3">
        <Box
          className="header justify-space-between flex"
          sx={{
            flexDirection: { xs: "column", md: "unset" },
          }}
        >
          <Box className="flex align-center">
            <Box className="upload-file-icon">
              <img
                alt="Mail"
                src="/icons/icon_file.png"
                width={20}
                height={20}
              />
            </Box>
            <Tooltip title={loanDocument?.documents?.documentName}>
              <Box className="flex-column align-center ml-3">
                <Typography variant="h3" className="title">
                  {loanDocument?.documents?.documentName}
                </Typography>

                {/* <Typography variant="body1" className="description">
                  {loanDocument?.documents?.documentDescription}
                </Typography> */}
              </Box>
            </Tooltip>
          </Box>

          <Box className="justify-end align-center">
            {loanDocument.documentStatus !== DocumentStatusCode.REQUESTED && (
              <>{getDocumentStatusColorCode(loanDocument.documentStatus)}</>
            )}
            <>
              {loanDocument?.isDownloaded && (
                <>
                  <BlackTooltip title="Upload">
                    <IconButton
                      className="ml-2 icon-button"
                      onClick={() => {
                        setSelectedDocument(loanDocument);
                        hiddenFileInput?.current?.click();
                      }}
                      disabled={isUploading}
                    >
                      {isUploading &&
                      selectedDocument.loanDocumentId ===
                        loanDocument.loanDocumentId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <img alt="Upload" src="/icons/icon_upload.png" />
                      )}
                    </IconButton>
                  </BlackTooltip>
                </>
              )}

              {loanDocument.blobFilePathToDownload !== "" && (
                <>
                  <BlackTooltip title="Download">
                    <IconButton
                      className="ml-2"
                      onClick={() => {
                        setSelectedDocument(loanDocument);
                        actionOnDowload(loanDocument);
                      }}
                      disabled={isDownloading}
                    >
                      {isDownloading &&
                      selectedDocument.loanDocumentId ===
                        loanDocument.loanDocumentId ? (
                        <CircularProgress size={20} />
                      ) : (
                        <img alt="Download" src="/icons/icon_download.png" />
                      )}
                    </IconButton>
                  </BlackTooltip>

                  <BlackTooltip title="View">
                    <IconButton
                      className="ml-2"
                      onClick={() => actionOnView(loanDocument)}
                      disabled={
                        loanDocument.blobFilePathToDownload === null &&
                        loanDocument.documentStatus ===
                          DocumentStatusCode.REQUESTED
                      }
                    >
                      <RemoveRedEyeIcon />
                    </IconButton>
                  </BlackTooltip>
                </>
              )}
            </>
          </Box>
        </Box>

        {loanDocument?.documents?.instructions !== null && (
          <Box className="upload-footer-section mt-3">
            <Box className="info-header flex align-center">
              <img
                alt="File"
                src="/icons/icon_info.png"
                width={20}
                height={20}
              />
              <Typography variant="h3" className="ml-2">
                Important Instructions
              </Typography>
            </Box>
            <Box className="important-note">
              {!loanDocument?.isDownloaded && (
                <>
                  {loanDocument?.documents?.instructions.length > 0 &&
                    loanDocument?.documents?.instructions.map(
                      (note: string) => (
                        <ul className="list">
                          <li>
                            <Typography variant="body1" className="note">
                              {note}
                            </Typography>
                          </li>
                        </ul>
                      )
                    )}
                </>
              )}

              {loanDocument?.isDownloaded && (
                <>
                  {loanDocument?.documents?.instructionsForDownload.length >
                    0 &&
                    loanDocument?.documents?.instructionsForDownload.map(
                      (note: string) => (
                        <ul className="list">
                          <li>
                            <Typography variant="body1" className="note">
                              {note}
                            </Typography>
                          </li>
                        </ul>
                      )
                    )}
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  const getAdditionalDocumentList = (
    loanDocument: UploadedDocumentsProps
  ): any => (
    <Box className="document-upload-wrapper">
      <Box className="document-upload-section py-3">
        <Box
          className="header justify-space-between flex"
          sx={{
            flexDirection: { xs: "column", md: "unset" },
          }}
        >
          <Box className="flex align-center">
            <Box className="upload-file-icon">
              <img
                alt="Mail"
                src="/icons/icon_file.png"
                width={20}
                height={20}
              />
            </Box>
            <Box className="flex-column ml-3">
              <Typography variant="h3" className="title">
                {loanDocument?.documents?.documentName}
              </Typography>
              {loanDocument.securityPropertyId !== null && (
                <Typography variant="body1" className="description">
                  Property {loanDocument?.propertyIndex + 1} -{" "}
                  {loanDocument?.propertyAddress}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="justify-end align-center">
            {loanDocument.documentStatus !== DocumentStatusCode.REQUESTED && (
              <>{getDocumentStatusColorCode(loanDocument.documentStatus)}</>
            )}
            <>
              {!loanDocument?.documents?.isAdminUploadForSign &&
                loanDocument.documentStatus !== DocumentStatusCode.ACCEPTED && (
                  <>
                    <BlackTooltip title="Upload">
                      <IconButton
                        className="ml-2 icon-button"
                        onClick={() => {
                          setSelectedDocument(loanDocument);
                          hiddenFileInput?.current?.click();
                        }}
                        disabled={isUploading}
                      >
                        {isUploading &&
                        selectedDocument.loanDocumentId ===
                          loanDocument.loanDocumentId ? (
                          <CircularProgress size={20} />
                        ) : (
                          <img alt="Upload" src="/icons/icon_upload.png" />
                        )}
                      </IconButton>
                    </BlackTooltip>
                  </>
                )}

              {loanDocument.blobFilePath !== "" && (
                <>
                  <BlackTooltip title="View">
                    <IconButton
                      className="ml-2"
                      onClick={() => actionOnView(loanDocument)}
                      disabled={
                        loanDocument.blobFilePathToDownload === null &&
                        loanDocument.documentStatus ===
                          DocumentStatusCode.REQUESTED
                      }
                    >
                      <RemoveRedEyeIcon />
                    </IconButton>
                  </BlackTooltip>
                </>
              )}
            </>
          </Box>
        </Box>
        {loanDocument.documentStatus !== DocumentStatusCode.ACCEPTED &&
          loanDocument.documentStatus !== DocumentStatusCode.UPLOADED && (
            <>
              {loanDocument?.documents?.instructions !== null && (
                <Box className="upload-footer-section mt-3">
                  <Box className="info-header flex align-center">
                    <img
                      alt="File"
                      src="/icons/icon_info.png"
                      width={20}
                      height={20}
                    />
                    <Typography variant="h3" className="ml-2">
                      Important Instructions
                    </Typography>
                  </Box>
                  <Box className="important-note">
                    {loanDocument?.documents?.instructions.length > 0 &&
                      loanDocument?.documents?.instructions.map(
                        (note: string) => (
                          <ul className="list">
                            <li>
                              <Typography variant="body1" className="note">
                                {note}
                              </Typography>
                            </li>
                          </ul>
                        )
                      )}
                  </Box>
                </Box>
              )}

              {loanDocument.adminApproval.status ===
                DocumentStatusCode.NEEDCLARIFICATION && (
                <Box className="upload-footer-section mt-3">
                  <Box className="info-header flex align-center status-container">
                    <img
                      alt="File"
                      src="/icons/icon_info.png"
                      width={20}
                      height={20}
                      style={{
                        filter: "invert(0%)",
                        color: "#dc3545",
                      }}
                    />
                    <Typography variant="h3" className="ml-2 rejected">
                      Clarification Required:
                    </Typography>
                  </Box>
                  <Box className="important-note pl-7">
                    <Typography variant="body1" className="note">
                      {loanDocument.adminApproval.reason}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
      </Box>
    </Box>
  );

  const getPropertyDocumentList = (
    loanDocument: UploadedDocumentsProps
  ): any => (
    <Box className="document-upload-wrapper">
      <Box className="document-upload-section py-3">
        <Box
          className="header justify-space-between flex"
          sx={{
            flexDirection: { xs: "column", md: "unset" },
          }}
        >
          <Box className="flex align-center">
            <Box className="upload-file-icon">
              <img
                alt="Mail"
                src="/icons/icon_file.png"
                width={20}
                height={20}
              />
            </Box>
            <Box className="flex-column  ml-3">
              <Typography variant="h3" className="title">
                {loanDocument?.documents?.documentName}
              </Typography>
              {loanDocument.securityPropertyId !== null && (
                <Typography variant="body1" className="description">
                  Property {loanDocument?.propertyIndex + 1} -{" "}
                  {loanDocument?.propertyAddress}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="justify-end align-center">
            {loanDocument.documentStatus !== DocumentStatusCode.REQUESTED && (
              <>{getDocumentStatusColorCode(loanDocument.documentStatus)}</>
            )}
            <>
              {!loanDocument?.documents?.isAdminUploadForSign &&
                loanDocument.documentStatus !== DocumentStatusCode.ACCEPTED && (
                  <>
                    <BlackTooltip title="Upload">
                      <IconButton
                        className="ml-2 icon-button"
                        onClick={() => {
                          setSelectedDocument(loanDocument);
                          hiddenFileInput?.current?.click();
                        }}
                        disabled={isUploading}
                      >
                        {isUploading &&
                        selectedDocument.loanDocumentId ===
                          loanDocument.loanDocumentId ? (
                          <CircularProgress size={20} />
                        ) : (
                          <img alt="Upload" src="/icons/icon_upload.png" />
                        )}
                      </IconButton>
                    </BlackTooltip>
                  </>
                )}

              {loanDocument.blobFilePath !== "" && (
                <>
                  <BlackTooltip title="View">
                    <IconButton
                      className="ml-2"
                      onClick={() => actionOnView(loanDocument)}
                      disabled={
                        (loanDocument.blobFilePathToDownload === null &&
                          loanDocument.documentStatus ===
                            DocumentStatusCode.REQUESTED) ||
                        isUploading
                      }
                    >
                      <RemoveRedEyeIcon />
                    </IconButton>
                  </BlackTooltip>
                </>
              )}
            </>
          </Box>
        </Box>
        {loanDocument.documentStatus !== DocumentStatusCode.ACCEPTED &&
          loanDocument.documentStatus !== DocumentStatusCode.UPLOADED && (
            <>
              {loanDocument?.documents?.instructions !== null && (
                <Box className="upload-footer-section mt-3">
                  <Box className="info-header flex align-center">
                    <img
                      alt="File"
                      src="/icons/icon_info.png"
                      width={20}
                      height={20}
                    />
                    <Typography variant="h3" className="ml-2">
                      Important Instructions
                    </Typography>
                  </Box>
                  <Box className="important-note">
                    {loanDocument?.documents?.instructions.length > 0 &&
                      loanDocument?.documents?.instructions.map(
                        (note: string) => (
                          <ul className="list">
                            <li>
                              <Typography variant="body1" className="note">
                                {note}
                              </Typography>
                            </li>
                          </ul>
                        )
                      )}
                  </Box>
                </Box>
              )}

              {loanDocument.adminApproval.status ===
                DocumentStatusCode.NEEDCLARIFICATION && (
                <Box className="upload-footer-section mt-3">
                  <Box className="info-header flex align-center status-container">
                    <img
                      alt="File"
                      src="/icons/icon_info.png"
                      width={20}
                      height={20}
                      style={{
                        filter: "invert(0%)",
                        color: "#dc3545",
                      }}
                    />
                    <Typography variant="h3" className="ml-2 rejected">
                      Clarification Required:
                    </Typography>
                  </Box>
                  <Box className="important-note pl-7">
                    <Typography variant="body1" className="note">
                      {loanDocument.adminApproval.reason}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
      </Box>
    </Box>
  );

  return (
    <>
      {loadingHistory && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!loadingHistory && loanDocumentsList.length > 0 && (
        <>
          {/* Upload Signed Documents */}
          {loanDocumentsList.filter(
            (loanDocument: UploadedDocumentsProps) =>
              loanDocument?.documents?.isAdminUploadForSign
          ).length > 0 && (
            <Box className="app-box-wrapper mt-5">
              <Box className="app-box-header header flex-column">
                <Typography variant="h2" className="f17">
                  Upload Signed Documents
                </Typography>
              </Box>

              {loanDocumentsList
                .filter(
                  (loanDocument: UploadedDocumentsProps) =>
                    loanDocument?.documents?.isAdminUploadForSign
                )
                .map(
                  (
                    loanDocument: UploadedDocumentsProps,
                    index: number,
                    filteredArray: UploadedDocumentsProps[]
                  ) => (
                    <Box
                      key={index}
                      className={`px-5 pt-5 ${
                        index === filteredArray.length - 1 ? "pb-5" : "pb-0"
                      }`}
                    >
                      {getSignedDocumentList(loanDocument)}
                    </Box>
                  )
                )}
            </Box>
          )}

          {/* Additional Documents */}
          {loanDocumentsList.filter(
            (loanDocument: UploadedDocumentsProps) =>
              loanDocument?.documents?.isAdminInitiated
          ).length > 0 && (
            <Box className="app-box-wrapper mt-5">
              <Box className="app-box-header header flex-column">
                <Typography variant="h2" className="f17">
                  Additional Documents
                </Typography>
              </Box>

              {loanDocumentsList
                .filter(
                  (loanDocument: UploadedDocumentsProps) =>
                    loanDocument?.documents?.isAdminInitiated
                )
                .map(
                  (
                    loanDocument: UploadedDocumentsProps,
                    index: number,
                    filteredArray: UploadedDocumentsProps[]
                  ) => (
                    <Box
                      key={index}
                      className={`px-5 pt-5 ${
                        index === filteredArray.length - 1 ? "pb-5" : "pb-0"
                      }`}
                    >
                      {getAdditionalDocumentList(loanDocument)}
                    </Box>
                  )
                )}
            </Box>
          )}

          {/* Property Documents */}
          <Box className="app-box-wrapper mt-5">
            <Box className="app-box-header header flex-column">
              <Typography variant="h2" className="f17">
                Property Documents
              </Typography>
            </Box>

            {loanDocumentsList
              .filter(
                (loanDocument: UploadedDocumentsProps) =>
                  loanDocument?.documents?.isDefault
              )
              .map(
                (
                  loanDocument: UploadedDocumentsProps,
                  index: number,
                  filteredArray: UploadedDocumentsProps[]
                ) => (
                  <Box
                    key={index}
                    className={`px-5 pt-5 ${
                      index === filteredArray.length - 1 ? "pb-5" : "pb-0"
                    }`}
                  >
                    {getPropertyDocumentList(loanDocument)}
                  </Box>
                )
              )}
          </Box>
          <input
            accept="application/pdf"
            type="file"
            ref={hiddenFileInput}
            onChange={(event) => {
              actionOnUpload(event);
              event.target.value = "";
            }}
            style={{ display: "none" }}
          />
          {showDocumentPreview && (
            <DocumentPreview
              showPdfViewer={showDocumentPreview}
              onCloseDocument={onCloseView}
              activeDocument={selectedDocument}
              loadDocumentsList={loadDocumentsList}
            />
          )}
        </>
      )}

      {loanDocumentsList.length === 0 && !loadingHistory && (
        <EmptyPlaceholder message="No documents found" />
      )}

      <Box mt={4}>
        <AppPrimaryButton
          title="Continue"
          isLoading={false}
          loadingText="Please wait.."
          onClick={() => {
            if (!statusPhysicalDocuments) {
              setShowThankyouDialog("physical");
              return;
            }
            if (statusDocuments === "progress") {
              setShowThankyouDialog("progress");
              return;
            }
            handleCompleteAndNext();
          }}
          disabled={statusDocuments === "pending"}
        />
      </Box>

      <ThankYouDialog
        showConfirm={showThankyouDialog}
        onCloseForm={() => {
          setShowThankyouDialog("");
          loadDocumentsList();
        }}
      />
    </>
  );
};

export default DocumentUpload;
